import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Button,
  Space,
  message,
  Typography,
  Popconfirm,
  Checkbox,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { teamService, Team } from '../services/teams';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import '../styles/resizable-table.css';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

const { Title } = Typography;

type SortOrder = 'ascend' | 'descend' | null;

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};

export const Teams: React.FC = () => {
  const listingKey = 'teams';
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const response = await teamService.getAll();
      setTeams(response.data || []);
    } catch {
      message.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/teams/new');
  };

  const handleEdit = (team: Team) => {
    navigate(`/teams/${team.id}/edit`);
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      await Promise.all(selectedRowKeys.map((key) => teamService.delete(Number(key))));
      message.success('Team deleted');
      setSelectedRowKeys([]);
      fetchTeams();
    } catch {
      message.error('Failed to delete team');
    } finally {
      setDeleting(false);
    }
  };

  const getCellValue = (record: Team, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  const baseColumns = useMemo(
    () => [
      { 
        title: 'Name', 
        dataIndex: 'name', 
        key: 'name', 
        width: 200,
        sorter: (a: Team, b: Team) => (a.name || '').localeCompare(b.name || ''),
      },
      { 
        title: 'Contact Number', 
        dataIndex: 'contact_number', 
        key: 'contact_number', 
        width: 180,
        sorter: (a: Team, b: Team) => (a.contact_number || '').localeCompare(b.contact_number || ''),
      },
      { 
        title: 'Email', 
        dataIndex: 'email', 
        key: 'email', 
        width: 250,
        sorter: (a: Team, b: Team) => (a.email || '').localeCompare(b.email || ''),
      },
      {
        title: 'Enabled',
        dataIndex: 'enabled',
        key: 'enabled',
        width: 120,
        sorter: (a: Team, b: Team) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
        render: (enabled: boolean) => (enabled ? 'Yes' : 'No'),
      },
    ],
    []
  );

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);

  const maxVisibleColumns = 8;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );

  const handleSort = useCallback((key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  }, [persistSort, sortKey, sortOrder]);
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.dataIndex === 'name') return (record: Team) => record.name;
    if (col.dataIndex === 'contact_number') return (record: Team) => record.contact_number;
    if (col.dataIndex === 'email') return (record: Team) => record.email;
    if (col.dataIndex === 'enabled') return (record: Team) => (record.enabled ? 'Yes' : 'No');
    return undefined as unknown as (record: Team) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<Team>(
    teams,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const tableColumns = columnsWithSearch as any[];
  const totalLabel = `Total: ${filteredData.length} ${filteredData.length === 1 ? 'team' : 'teams'}`;
  const totalColumnCount = tableColumns.length;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      setColumnWidthByKey((prev) => {
        const next = { ...prev };
        keys.forEach((key, index) => {
          next[key] = updated[index];
        });
        return next;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = tableColumns.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, tableColumns, sortKey, sortOrder]);

  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<Team>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
  });

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateScrollbar = () => {
      const nextWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextWidth);
    };
    updateScrollbar();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateScrollbar);
      return () => window.removeEventListener('resize', updateScrollbar);
    }
    const observer = new ResizeObserver(updateScrollbar);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const hideTitle = location.pathname.startsWith('/settings');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {!hideTitle ? (
          <Title level={3} style={{ margin: 0 }}>
            Teams
          </Title>
        ) : (
          <div />
        )}
        <Space>
          <ColumnVisibilityToggle
            columns={columnConfig}
            visibleKeys={visibleKeys}
            onChange={handleColumnVisibilityChangeLimited}
            maxVisible={maxVisibleColumns}
          />
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} team${selectedRowKeys.length === 1 ? '' : 's'}?`}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleBatchDelete}
            >
              <Button danger loading={deleting}>
                Batch Delete
              </Button>
            </Popconfirm>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Team
          </Button>
        </Space>
      </div>

        <div className="flowbite-listing-table-shell">
          <div style={{ paddingRight: scrollbarWidth }}>
            <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableHeadCell className="w-12">
                  <Checkbox
                    checked={sortedData.length > 0 && selectedRowKeys.length === sortedData.length}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedRowKeys(sortedData.map((row) => row.id));
                      } else {
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </TableHeadCell>
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  const isSortable = typeof col.sorter === 'function';
                  const isSorted = sortKey === key;
                    const sortIndicator =
                      isSorted && sortOrder
                        ? sortOrder === 'ascend'
                          ? '▲'
                          : '▼'
                        : '';
                    const isFirstDataColumn = index === 0;
                    const maxWidth =
                      availableWidth != null
                        ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                        : undefined;

                    return (
                      <FlowbiteResizableHeader
                        key={key}
                        width={columnWidths[index]}
                        maxWidth={maxWidth}
                        onResize={handleResize(index)}
                        onClick={() => handleSort(key, col.sorter)}
                        className={isSortable ? 'cursor-pointer select-none' : undefined}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div>{col.title}</div>
                            {isFirstDataColumn && (
                              <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                            )}
                          </div>
                          {isSortable && (
                            <span className="text-xs text-gray-400">{sortIndicator}</span>
                          )}
                        </div>
                    </FlowbiteResizableHeader>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHeadCell className="w-12" />
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  return (
                      <TableHeadCell key={`${key}-search`} className="py-2">
                        {searchRowCells?.[index] ?? null}
                      </TableHeadCell>
                    );
                  })}
                </TableRow>
              </TableHead>
            </FlowbiteTable>
          </div>
          <div
            className="flowbite-listing-table-scroll"
            ref={tableScrollRef}
            tabIndex={0}
            onKeyDown={handleTableKeyDown}
          >
            <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable>
              <colgroup>
                <col style={{ width: 48 }} />
                {tableColumns.map((col, index) => (
                  <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
                ))}
              </colgroup>
              <TableBody className="divide-y">
                {loading && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && sortedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                      No teams
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  sortedData.map((record: Team, index: number) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const isFocused = focusedRowId === record.id;
                    return (
                      <TableRow
                        key={record.id}
                        data-row-id={record.id}
                        className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                        onDoubleClick={() => handleEdit(record)}
                        onClick={() => handleRowClick(record.id)}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => {
                              setSelectedRowKeys((prev) =>
                                prev.includes(record.id)
                                  ? prev.filter((key) => key !== record.id)
                                  : [...prev, record.id]
                              );
                            }}
                          />
                        </TableCell>
                        {tableColumns.map((col, index) => {
                          const value = getCellValue(record, col);
                          const content = typeof col.render === 'function'
                            ? col.render(value, record, index)
                            : value ?? '-';
                          return (
                            <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </FlowbiteTable>
          </div>
        </div>

    </div>
  );
};
